import { html, nothing } from 'lit';
import type { TemplateResult } from 'lit';
import type { StemDef } from '@/state/models';
import type { StemGainState } from '@/audio/engine';

export interface StemRowCallbacks {
  onVolume: (stem: StemDef, value: number) => void;
  onToggleMute: (stem: StemDef) => void;
  onToggleSolo: (stem: StemDef) => void;
}

export function renderStemRow(
  stem: StemDef,
  state: StemGainState | undefined,
  anySoloed: boolean,
  disabled: boolean,
  cb: StemRowCallbacks,
): TemplateResult {
  const volume = state?.volume ?? 1;
  const muted = state?.muted ?? false;
  const soloed = state?.soloed ?? false;
  const dimmed = !disabled && (muted || (anySoloed && !soloed));

  // Value readout format: "0.00", two decimals.
  const valueStr = volume.toFixed(2);

  return html`
    <div
      class="stem-row ${dimmed ? 'dimmed' : ''}"
      style="--stem-color: var(${stem.colorVar})"
    >
      <label class="stem-label" for="slider-${stem.name}">${stem.title}</label>
      <div class="stem-controls">
        <input
          id="slider-${stem.name}"
          class="slider"
          type="range"
          min="0"
          max="1"
          step="0.01"
          .value=${String(volume)}
          aria-label="${stem.title} level"
          aria-valuemin="0"
          aria-valuemax="1"
          aria-valuenow=${volume}
          aria-valuetext="${Math.round(volume * 100)} percent"
          ?disabled=${disabled}
          @input=${(e: Event) => {
            const target = e.target as HTMLInputElement;
            cb.onVolume(stem, Number(target.value));
          }}
        />
        <span class="stem-value" aria-hidden="true">${valueStr}</span>
        <button
          class="sm-btn s-btn"
          type="button"
          aria-label="Solo ${stem.title} stem"
          aria-pressed=${soloed ? 'true' : 'false'}
          ?disabled=${disabled}
          @click=${() => cb.onToggleSolo(stem)}
        >
          S
        </button>
        <button
          class="sm-btn m-btn"
          type="button"
          aria-label="Mute ${stem.title} stem"
          aria-pressed=${muted ? 'true' : 'false'}
          ?disabled=${disabled}
          @click=${() => cb.onToggleMute(stem)}
        >
          M
        </button>
      </div>
      ${nothing}
    </div>
  `;
}
